/**
 * Telegram-style emoji-picker для composer'а чата.
 *
 * Apple-стиль (как в реакциях TG) — выглядит цветно и единообразно
 * на всех платформах. Спрайт + chunks прогреваются заранее, чтобы первое
 * открытие picker'а было мгновенным — но НЕ при загрузке модуля (на телефоне
 * это конкурировало с главным JS и первой лентой), а из ChatPage после тихого
 * старта: prefetchEmojiPicker() (16.09.2026).
 *
 * 2026-05-18: spritesheet SELF-HOSTED (/emoji/apple-64.png, 4.4MB) — раньше
 * грузился с jsdelivr CDN, что давало 1-3 секунды задержки на каждом первом
 * открытии. Native emoji юзеру НЕ нужны — оставляем sprite, но с нашего origin.
 */
import { lazy, Suspense, useEffect } from "react";


type EmojiData = { native: string };

// Self-hosted sprite — без CDN-задержек, immutable cache от nginx.
// 16.09.2026: лист 32px для обычных мониторов, 64px для ретины (lib/emojiSprite).
import { appleSpriteUrl } from "@/lib/emojiSprite";
const APPLE_SPRITE_URL = appleSpriteUrl();

const PickerLazy = lazy(async () => {
  const [pickerMod, dataMod, i18nMod] = await Promise.all([
    import("@emoji-mart/react"),
    import("@emoji-mart/data/sets/15/apple.json"),
    import("@emoji-mart/data/i18n/ru.json"),
  ]);
  const PickerComp = pickerMod.default;
  const data = dataMod.default;
  const i18n = i18nMod.default;
  return {
    default: (props: Record<string, unknown>) => (
      <PickerComp data={data} i18n={i18n} {...props}/>
    ),
  };
});


let prefetched = false;
function prefetchAll() {
  if (prefetched) return;
  prefetched = true;
  void import("@emoji-mart/react");
  void import("@emoji-mart/data/sets/15/apple.json");
  void import("@emoji-mart/data/i18n/ru.json");
  if (typeof document !== "undefined") {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = APPLE_SPRITE_URL;
    document.head.appendChild(link);
    const img = new Image();
    img.src = APPLE_SPRITE_URL;
  }
}
/** Прогрев чанков пикера и спрайта (4,4 МБ). Зовёт ChatPage, когда первый
 *  экран отрисован и сеть свободна; повторные вызовы — no-op. */
export function prefetchEmojiPicker(): void {
  prefetchAll();
}


export function ChatEmojiPicker({
  onSelect, theme,
}: {
  onSelect: (native: string) => void;
  theme: "light" | "dark";
}) {
  // На повторном mount picker'а — chunks и спрайт уже в кэше, открытие мгновенное
  useEffect(() => { prefetchAll(); }, []);
  return (
    <Suspense fallback={<div className="chat-emoji-picker-loading">Загрузка…</div>}>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <PickerLazy
        {...({
          onEmojiSelect: (e: EmojiData) => onSelect(e.native),
          theme,
          locale: "ru",
          // Apple-стиль спрайтов (как в реакциях). Спрайт префетчен при старте app.
          set: "apple",
          // SELF-HOSTED sprite: emoji-mart берёт URL из этого callback вместо
          // hardcoded jsdelivr CDN. spritesheet=true → все emoji (включая
          // individual) рендерятся через sprite (не качают отдельные PNG).
          getSpritesheetURL: () => APPLE_SPRITE_URL,
          spritesheet: true,
          previewPosition: "none",
          skinTonePosition: "none",
          navPosition: "bottom",
          perLine: 8,
          maxFrequentRows: 1,
          dynamicWidth: false,
          emojiSize: 28,
          emojiButtonSize: 36,
        } as Record<string, unknown>)}
      />
    </Suspense>
  );
}
